/**
 * useExecutionLifecycle
 *
 * App-shell-level listener that translates live AppSync envelopes into updates
 * on ``useAppStore``'s ``executions`` registry. Mount **once** from
 * ``WorkbenchShell``; unmounting tears down the window listener cleanly.
 *
 * Scope: **Runtime SFN executions only** (per-network state machines — the
 * Run tab, future Agentic Network rooms). Brain-SFN (``stepflow-brain-*``)
 * lifecycle events are filtered out below and never touch the registry;
 * BuildTab owns Brain history as local REST state. See
 * ``doc/arch/vertical/NETWORK-TO-ROOMS.md`` for the domain model and the rationale.
 *
 * Event fan-in:
 *   The agent channel subscription lives in ``NetworkWorkbench`` via
 *   ``useAgentChannel``. Deduped envelopes are rebroadcast as ``window``
 *   CustomEvents with name ``'websocket-message'`` (see
 *   ``services/workbenchRealtime.ts::dispatchWorkbenchRealtime``). This hook
 *   is the single consumer responsible for mapping them onto execution state.
 *
 * Why this hook does not open its own subscription:
 *   We deliberately piggyback on the existing NetworkWorkbench subscription to
 *   avoid a second WebSocket per active network. Consequence: execution rows
 *   update only while the user is inside the workbench shell, which is fine —
 *   public / unauthenticated routes have no need for live execution state.
 *
 * Recognised event types (all carried as top-level ``eventType`` on the
 * canonical envelope produced by ``stepflow_core.events`` +
 * ``sfn_appsync_bridge``):
 *
 *   EXECUTION_STARTED    → status: RUNNING, startedAt from payload.startDate
 *   EXECUTION_COMPLETED  → status: SUCCEEDED, endedAt from payload.stopDate
 *   EXECUTION_FAILED     → status: {FAILED|TIMED_OUT|ABORTED}, error/cause captured
 *   SFN_TRANSITION       → non-terminal transitions; bumps updatedAt but does
 *                          not regress terminal rows (guarded in the slice)
 *   APPROVAL_REQUESTED   → status: AWAITING_APPROVAL (only if currently RUNNING)
 *   APPROVAL_RESOLVED    → status: RUNNING (only if currently AWAITING_APPROVAL)
 *
 * Any envelope without a ``networkId`` + ``executionId`` (or legacy ``taskId``)
 * is ignored — we can't key it into the registry.
 */
import { useEffect } from 'react';
import { useAppStore, TERMINAL_EXECUTION_STATUSES } from '../stores/useAppStore';

/** SFN detail.status strings that mean "this execution is done". */
const TERMINAL_SFN_STATUSES = new Set(['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED']);

/**
 * Brain SFN ARNs all contain this marker (``stepflow-brain-<env>``). Runtime
 * SFNs are named after the network and never collide with this substring.
 * The check is intentionally lenient on env suffix so it matches ``-dev``,
 * ``-staging``, ``-prod``, etc., and also any historical renames.
 */
const BRAIN_SFN_MARKER = 'stepflow-brain';

/**
 * Return ``true`` when the envelope belongs to the Brain SFN and must be
 * excluded from the runtime execution registry. We look at payload first
 * (where ``sfn_appsync_bridge`` places ``stateMachineArn``) then at the
 * envelope top level for older publishers.
 *
 * If we can't find a ``stateMachineArn`` at all we fall through to
 * ``false`` — i.e. allow the event to register. That keeps us compatible
 * with publishers that predate the bridge, at the cost of a registry row
 * we'd rather not have; the monotonic guard in the slice prevents that
 * row from corrupting status downstream.
 */
function isBrainEnvelope(detail) {
  const payload = detail?.payload && typeof detail.payload === 'object' ? detail.payload : null;
  const arn =
    (payload && typeof payload.stateMachineArn === 'string' ? payload.stateMachineArn : null)
    || (typeof detail?.stateMachineArn === 'string' ? detail.stateMachineArn : null);
  if (!arn) return false;
  return arn.toLowerCase().includes(BRAIN_SFN_MARKER);
}

/** Pull the first non-empty string from a list of candidate fields. */
function pickStr(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * Extract ``{ taskId, networkId, poolId, executionArn, startedAt, endedAt }``
 * from a dispatched ``'websocket-message'`` detail. The canonical envelope
 * exposes these at the top level; ``payload`` holds SFN-specific extras like
 * ``stateMachineArn``, ``startDate``, ``stopDate``, ``error``, ``cause``.
 *
 * The legacy ``status_update`` mirror some publishers still emit is *not*
 * handled here — BuildTab's existing handlers own those. We only key off the
 * canonical ``eventType`` to avoid racing two competing update paths.
 */
function deriveExecutionMeta(detail) {
  const payload = detail?.payload && typeof detail.payload === 'object' && !Array.isArray(detail.payload)
    ? detail.payload
    : {};
  const taskId =
    pickStr(detail, 'executionId', 'taskId')
    || pickStr(payload, 'executionId', 'taskId');
  const networkId = pickStr(detail, 'networkId') || pickStr(payload, 'networkId');
  if (!taskId || !networkId) return null;
  return {
    taskId,
    networkId,
    poolId: pickStr(detail, 'poolId') || pickStr(payload, 'poolId'),
    executionArn: pickStr(payload, 'executionArn') || pickStr(detail, 'executionArn'),
    startedAt:
      pickStr(payload, 'startDate', 'startedAt')
      || pickStr(detail, 'startDate', 'startedAt'),
    endedAt:
      pickStr(payload, 'stopDate', 'endedAt')
      || pickStr(detail, 'stopDate', 'endedAt'),
    startedBy: pickStr(detail, 'actorSub', 'userId'),
    error: pickStr(payload, 'error'),
    cause: pickStr(payload, 'cause'),
    sfnStatusString: pickStr(payload, 'status') || pickStr(detail, 'status'),
  };
}

/**
 * Map the raw SFN detail.status onto our registry's ExecutionStatus taxonomy.
 * Called only for ``SFN_TRANSITION`` envelopes (where we don't already know
 * the category from the eventType).
 */
function mapSfnStatus(raw) {
  const s = String(raw || '').toUpperCase();
  if (s === 'RUNNING') return 'RUNNING';
  if (s === 'SUCCEEDED') return 'SUCCEEDED';
  if (s === 'FAILED') return 'FAILED';
  if (s === 'TIMED_OUT') return 'TIMED_OUT';
  if (s === 'ABORTED') return 'ABORTED';
  return null; // Not meaningful to the registry — ignore.
}

export default function useExecutionLifecycle() {
  useEffect(() => {
    const onMsg = (ev) => {
      const detail = ev?.detail;
      if (!detail || typeof detail !== 'object') return;

      const eventType = String(detail.eventType || '').toUpperCase();
      if (!eventType) return;

      // Fast-path gate: only a handful of event types change execution state.
      // Everything else (logs, agent messages, presence, …) is noise here.
      const relevant =
        eventType === 'EXECUTION_STARTED'
        || eventType === 'EXECUTION_COMPLETED'
        || eventType === 'EXECUTION_FAILED'
        || eventType === 'SFN_TRANSITION'
        || eventType === 'APPROVAL_REQUESTED'
        || eventType === 'APPROVAL_RESOLVED';
      if (!relevant) return;

      // Domain isolation: Brain SFN lifecycle events share this channel with
      // Runtime SFN events but belong in BuildTab, not in the runtime
      // registry. Drop them here so RunnerView's status widget and any
      // future Agentic Network consumer see *only* runtime transitions.
      if (isBrainEnvelope(detail)) return;

      const meta = deriveExecutionMeta(detail);
      if (!meta) return;

      const { upsertExecution, setExecutionStatus } = useAppStore.getState();

      if (eventType === 'EXECUTION_STARTED') {
        upsertExecution({
          taskId: meta.taskId,
          networkId: meta.networkId,
          poolId: meta.poolId,
          executionArn: meta.executionArn,
          startedAt: meta.startedAt,
          startedBy: meta.startedBy,
          status: 'RUNNING',
          source: 'live',
        });
        return;
      }

      if (eventType === 'EXECUTION_COMPLETED') {
        upsertExecution({
          taskId: meta.taskId,
          networkId: meta.networkId,
          poolId: meta.poolId,
          executionArn: meta.executionArn,
          endedAt: meta.endedAt,
          status: 'SUCCEEDED',
          source: 'live',
        });
        return;
      }

      if (eventType === 'EXECUTION_FAILED') {
        // Map the underlying SFN status string through so TIMED_OUT / ABORTED
        // keep their specificity; fall back to FAILED when upstream only gave
        // us a generic failure signal.
        const specific = mapSfnStatus(meta.sfnStatusString);
        const status =
          specific && TERMINAL_EXECUTION_STATUSES.has(specific) && specific !== 'SUCCEEDED'
            ? specific
            : 'FAILED';
        upsertExecution({
          taskId: meta.taskId,
          networkId: meta.networkId,
          poolId: meta.poolId,
          executionArn: meta.executionArn,
          endedAt: meta.endedAt,
          status,
          error: meta.error,
          cause: meta.cause,
          source: 'live',
        });
        return;
      }

      if (eventType === 'SFN_TRANSITION') {
        const mapped = mapSfnStatus(meta.sfnStatusString);
        if (!mapped) return;
        upsertExecution({
          taskId: meta.taskId,
          networkId: meta.networkId,
          poolId: meta.poolId,
          executionArn: meta.executionArn,
          startedAt: meta.startedAt,
          endedAt: TERMINAL_SFN_STATUSES.has(meta.sfnStatusString) ? meta.endedAt : undefined,
          status: mapped,
          source: 'live',
        });
        return;
      }

      if (eventType === 'APPROVAL_REQUESTED') {
        // Only lift a row into AWAITING_APPROVAL if we already know it's running;
        // otherwise the approval event predates this client's knowledge of the
        // SFN and the BuildTab REST rehydration path will fill the row in with
        // accurate state shortly. (Registry is a projection, not a materialised
        // view — we don't try to invent rows from approvals alone.)
        const { executions } = useAppStore.getState();
        const prev = executions[meta.taskId];
        if (!prev || prev.status !== 'RUNNING') return;
        setExecutionStatus(meta.taskId, 'AWAITING_APPROVAL');
        return;
      }

      if (eventType === 'APPROVAL_RESOLVED') {
        const { executions } = useAppStore.getState();
        const prev = executions[meta.taskId];
        if (!prev || prev.status !== 'AWAITING_APPROVAL') return;
        setExecutionStatus(meta.taskId, 'RUNNING');
        return;
      }
    };

    window.addEventListener('websocket-message', onMsg);
    return () => window.removeEventListener('websocket-message', onMsg);
  }, []);
}
